import React, { createContext, useContext, useState, useCallback } from 'react';
import { Box, Paper, Typography, IconButton, Slide } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const ToastNotificationsContext = createContext();

export const useToastNotifications = () => {
    const context = useContext(ToastNotificationsContext);
    if (!context) {
        throw new Error('useToastNotifications must be used within a ToastNotificationsProvider');
    }
    return context;
};

const ToastNotification = ({ notification, onClose, onClick }) => {
    const formatTime = (timestamp) => {
        const date = new Date(timestamp);
        return date.toLocaleTimeString('fr-FR', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <Slide direction="left" in={true} mountOnEnter unmountOnExit>
            <Paper
                elevation={8}
                onClick={onClick}
                sx={{
                    position: 'fixed',
                    bottom: 40,
                    right: 40,
                    width: 350,
                    p: 2,
                    mb: notification.index * 10, // Espacement entre les notifications
                    cursor: 'pointer',
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'primary.main',
                    borderRadius: 2,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    zIndex: 9999,
                    '&:hover': {
                        boxShadow: '0 12px 40px rgba(0,0,0,0.16)',
                        transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography
                            variant="subtitle2"
                            sx={{
                                fontWeight: 600,
                                color: 'primary.main',
                                mb: 1
                            }}
                        >
                            Vous avez reçu un nouveau message
                        </Typography>

                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 500,
                                color: 'text.primary',
                                mb: 0.5
                            }}
                        >
                            {notification.senderInfo?.username || 'Utilisateur'} - {notification.senderInfo?.erpName || 'ERP'}
                        </Typography>

                        <Typography
                            variant="body2"
                            sx={{
                                color: 'text.secondary',
                                mb: 1,
                                wordBreak: 'break-word'
                            }}
                        >
                            {notification.message && notification.message.length > 100
                                ? `${notification.message.substring(0, 100)}...`
                                : notification.message || 'Nouveau message'
                            }
                        </Typography>

                        <Typography
                            variant="caption"
                            sx={{
                                color: 'text.disabled',
                                fontStyle: 'italic'
                            }}
                        >
                            {formatTime(notification.timestamp)}
                        </Typography>
                    </Box>

                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            onClose();
                        }}
                        sx={{
                            ml: 1,
                            color: 'text.disabled',
                            '&:hover': { color: 'text.primary' }
                        }}
                    >
                        <Close fontSize="small" />
                    </IconButton>
                </Box>
            </Paper>
        </Slide>
    );
};

export const ToastNotificationsProvider = ({ children }) => {
    const [toastNotifications, setToastNotifications] = useState([]);
    const navigate = useNavigate();

    const addToastNotification = useCallback((message, senderInfo) => {
        // Extraire le contenu du message de manière sécurisée
        const messageContent = message.message || message.content || message.text || '';

        const notification = {
            id: `toast-${message.id}-${Date.now()}`,
            message: messageContent,
            senderInfo,
            timestamp: message.createdAt || message.timestamp || Date.now(),
            conversationId: message.conversationId,
        };

        setToastNotifications(prev => {
            // Éviter les doublons
            const exists = prev.find(n => n.id === notification.id);
            if (exists) return prev;

            const newNotifications = [notification, ...prev].map((notif, index) => ({
                ...notif,
                index
            }));

            // Garder max 3 notifications
            return newNotifications.slice(0, 3);
        });

        // Auto-suppression après 10 secondes
        setTimeout(() => {
            removeToastNotification(notification.id);
        }, 10000);
    }, []);

    const removeToastNotification = useCallback((notificationId) => {
        setToastNotifications(prev =>
            prev.filter(notification => notification.id !== notificationId)
                .map((notif, index) => ({ ...notif, index }))
        );
    }, []);

    const handleToastClick = useCallback((notification) => {
        // Naviguer vers la conversation avec le bon format
        navigate('/gestion/messagerie', {
            state: { conversationId: notification.conversationId }
        });
        // Supprimer la notification
        removeToastNotification(notification.id);
    }, [navigate, removeToastNotification]);

    const contextValue = {
        addToastNotification,
        removeToastNotification,
    };

    return (
        <ToastNotificationsContext.Provider value={contextValue}>
            {children}

            {/* Rendu des notifications toast */}
            {toastNotifications
                .filter(notification => notification && notification.id && notification.senderInfo)
                .map((notification) => (
                    <ToastNotification
                        key={notification.id}
                        notification={notification}
                        onClose={() => removeToastNotification(notification.id)}
                        onClick={() => handleToastClick(notification)}
                    />
                ))
            }
        </ToastNotificationsContext.Provider>
    );
}; 